import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
    format,
    set,
    // parseISO
} from "date-fns";
import useGetStatistics, {
    EModules,
    EOrderingReports,
    // IAPIAdvancedReports,
    // TAPIAdvancedReportsV2,
    TAdvancedReportOrderKeys,
    TAdvancedReportResultsV2,
    getStatisticsAsync,
} from "api/useGetStatistics";

import DateRange, { IDateRangeProps } from "components/DateRange";
import { IDropdownFilterKeyPair } from "components/Table";
import {
    TActionProps,
    TGeneratePdfFunction,
    TGenerateXLSFunction,
} from "components/Table/components/ActionButtons/types";
import {
    IRangeFilterProps,
} from "components/Table/components/CustomizeFilters/types";
import { ESortOrder, ISortState } from "components/Table/types";
import Tabs from "components/Tabs";
import Typography from "components/Typography";
import apiDateFormat from "constants/apiDateFormat";
import {
    // IDateRange,
    commonDateRangesV2,
    getDateRangeByKey
} from "constants/commonDateRanges";
import { useDebounce } from "hooks";
import styles from "pages/Reporting/styles.module.scss";
// import EDaysOnLot from "types/APIDaysOnLot";
import { IProviderCheckList } from "types/IProviderCheckList";
import { EQueryParameterKeys, ERoutesAnchors } from "types/Routes";
import {
    ELocalSavedFilters,
    ITableWithProviders,
    getDefaultFilters,
    setDefaultFiltersV2,
    tableFiltersWithProviders,
} from "utils/defaultFilters";
import { filterColumnsV2 } from "utils/filterColumns";
import formatOrderingAPI from "utils/formatOrderingAPI";
import {
    getColumns,
    getPdfColumns,
    getXLSColumns,
    tablePDFFileNames,
    tableTabs,
} from "./constants";
import { capitalizeFirstLetter, formattedDateRange, onGeneratePDFDefaultType, reportNaming } from "utils/helper";
import useUrlQuery from "hooks/useUrlQuery";
import { ReportingUrlParams } from "utils/urlParams";
import { useAppSelector } from "hooks/store";
import { selectUserData } from "store/auth/authSlice";
import DataTable from "components/v2/DataTable";
import { Box, Text } from "@chakra-ui/react";
import { ActionButtonV2 } from "components/Table/components/ActionButtons";
import { CustomizeProvider } from "components/v2/CustomizeProvider";
import { ColumnSizingState, PaginationState, SortingState, VisibilityState } from "@tanstack/react-table";
import DolSelect from "components/v2/Select/DolSelect";
import usePostUsers, { EUserModules } from "api/usePostUsers";
import { TDateRangeOption } from "types/APIParameters";

const ReportingTabs = () => {

    // const dispatch = useAppDispatch();

    // const reportingTab = useAppSelector(selectReportingTabData);

    const { mutate: savePagePreference } = usePostUsers<EUserModules.PagePreference>({
        module: EUserModules.PagePreference,
    });

    const { getParam, getMultipleParams, setMultipleParams, removeMultipleParams } = useUrlQuery<ReportingUrlParams>();

    const startDateParams = getParam("startDate");
    const endDateParams = getParam("endDate");
    const dolRangeParams = getParam("dolRange");
    const sourceParams = getMultipleParams("source");
    const sortByParams = getParam("sortBy");
    const searchParams = getParam("search");
    const selectedDolRangeParams = getParam("selectedDolRange");
    const activeTableParams = getParam("activeTable");
    const leadsZeroParams = getParam("leadsZero");
    const dateRangeParams = getParam("date_range");

    // useEffect(() => {
    //   if (activeTableParams) {
    //   dispatch(setActiveTab(activeTableParams as EOrderingReports));
    //   }
    // }, []);

    const currentTableFilter = useMemo((): ELocalSavedFilters | null => {
        switch (activeTableParams as EOrderingReports) {
            case EOrderingReports.DaysOnLot:
                return ELocalSavedFilters.DaysOnLotTable;

            case EOrderingReports.LeastVDPs:
                return ELocalSavedFilters.LeastVDPTable;

            case EOrderingReports.MostVDPs:
                return ELocalSavedFilters.MostVDPTable;

            case EOrderingReports.MostLeads:
                return ELocalSavedFilters.MostLeadsTable;
            // case EOrderingReports.VDPBoost:
            //     return ELocalSavedFilters.VDPBoostTable;
            case EOrderingReports.VDPSDOL:
                return ELocalSavedFilters.VDPsDOLTable;
            default:
                return null;
        }
    }, [activeTableParams]);

    const defaultFilters = useMemo(() => {
        if (currentTableFilter) {
            return getDefaultFilters<ITableWithProviders>(
                currentTableFilter,
                tableFiltersWithProviders
            );
        }

        return tableFiltersWithProviders;
    }, [currentTableFilter]);

    const [dateRange, setDateRange] = useState<IDateRangeProps["value"]>(
        {
            startDate: startDateParams ? new Date(startDateParams) : dateRangeParams ? getDateRangeByKey(dateRangeParams)?.startDate : commonDateRangesV2[2].value.startDate,
            endDate: endDateParams ? new Date(endDateParams) : dateRangeParams ? getDateRangeByKey(dateRangeParams)?.endDate : commonDateRangesV2[2].value.endDate,
            key: dateRangeParams || 'all_time'
        }
    );

    const [page, setPage] = useState<number>(0);
    const [pageSize, setPageSize] = useState<number>(defaultFilters.pageSize);

    const [providers, setProviders] = useState<IProviderCheckList[]>(
        // defaultFilters.providers
        []
    );

    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
        defaultFilters.columnVisibility && Object.keys(defaultFilters.columnVisibility).length > 0 ? defaultFilters.columnVisibility : { vin: false }
    );

    const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(defaultFilters.columnSizing || {});


    useEffect(() => {
        if (defaultFilters.pageSize) {
            setPageSize(defaultFilters.pageSize);
        }
        if (defaultFilters.columnVisibility && Object.keys(defaultFilters.columnVisibility).length > 0) {
            setColumnVisibility(defaultFilters.columnVisibility);
        }
        if (defaultFilters.columnSizing) {
            setColumnSizing(defaultFilters.columnSizing || {});
        }
    },[activeTableParams]);
    
    const dolOptions: IDropdownFilterKeyPair[] = [
        {
            label: 'None',
            value: '0',
        },
        {
            label: 'Over 7',
            value: '7',
        },
        {
            label: 'Over 15',
            value: '15',
        },
        {
            label: 'Over 30',
            value: '30',
        },
        {
            label: 'Over 60',
            value: '60',
        },
    ];
    
    const vdpsDolOptions: IDropdownFilterKeyPair[] = [
        {
            label: 'None',
            value: '0',
        },
        {
            label: 'Over 15',
            value: '15',
        },
        {
            label: 'Over 30',
            value: '30',
        },
        {
            label: 'Over 60',
            value: '60',
        },
        {
            label: 'Over 90',
            value: '90',
        },
    ];


    const findOption = selectedDolRangeParams ? dolOptions.find((option) => option.value === parseInt(selectedDolRangeParams)) : dolOptions[0];

    const [selectedDol, setSelectedDol] = useState<IDropdownFilterKeyPair>(findOption ?? dolOptions[0]);

    const [sortBy, setSortBy] = useState<SortingState>(sortByParams && typeof sortByParams === 'string' ? [{
        id: sortByParams.replace("-", ""),
        desc: sortByParams.startsWith("-") ? true : false,
    }] : []);

    const [quickSearch, setQuickSearch] = useState<string>(searchParams ?? "");

    const [dolRange, setDolRange] = useState<IRangeFilterProps["value"]>(
        dolRangeParams
            ? (dolRangeParams.split("-").map(Number) as [number, number])
            : [1, 2000]
    );

    const quickSearchApi = useDebounce<string>(quickSearch, 350);

    const userAuth = useAppSelector(selectUserData);

    useEffect(() => {
        const updatedProviders = [
            ...userAuth.dealerV2.third_party_provider
            .filter((provider) => userAuth.dealerV2.is_lite ? provider.key === 'CGR' : true)
            .map((provider) => {
                const defaultProvider = defaultFilters.providers.find(({ id }) => id === provider.key);
                return {
                    id: provider.key,
                    label: provider.label,
                    value: userAuth.dealerV2.is_lite ? true : defaultProvider ? defaultProvider.value : false// Default to false for other providers
                };
            }),
        ] as IProviderCheckList[];

        setProviders(updatedProviders);
    }, [userAuth.dealerV2.third_party_provider, defaultFilters]);

    // useMemo(() => {

    //     const providers = userAuth.dealerV2.third_party_provider.map((provider) => ({
    //         id: provider.key,
    //         label: provider.label,
    //         value: sourceParams?.includes(provider.key) || false,
    //     })) as IProviderCheckList[]; 
  
    //     setProviders(providers);
  
    //   // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [userAuth.dealerV2.uid]);

    const sortedBy: ISortState = useMemo(() => {

        if (!sortBy.length) {
            return {
                column: null,
                order: ESortOrder.None,
            };
        }
        return {
            column: sortBy[0].id as TAdvancedReportOrderKeys,
            order: sortBy[0].desc ? ESortOrder.Descending : ESortOrder.Ascending,
        };
    }, [sortBy]);

    const fetchParameters = useMemo(
        () => ({
            module: EModules.AdvancedReportsV2,
            params: {
                ...(selectedDol.value !== '0' && {
                    dol_max: 2000,
                    dol_min: selectedDol.value,
                }),
                ...((activeTableParams === EOrderingReports.DaysOnLot ||
                    activeTableParams === EOrderingReports.MostLeads) &&
                    selectedDol.value === '0' && {
                    dol_max: dolRangeParams
                        ? isNaN(dolRangeParams.split("-").map(Number)[1])
                            ? 2000
                            : dolRangeParams.split("-").map(Number)[1]
                        : 2000,
                    dol_min: dolRangeParams
                        ? dolRangeParams.split("-").map(Number)[0]
                        : 0,
                }),
                date_from: format(dateRange.startDate, apiDateFormat),
                date_to: format(dateRange.endDate, apiDateFormat),
                // dol_range: daysOnLot,
                dealership_uid: userAuth.dealerV2.uid,
                page: page + 1,
                // limit: pageSize,
                // offset,
                page_size: pageSize,
                order: activeTableParams,
                ordering: formatOrderingAPI<TAdvancedReportOrderKeys>(sortedBy),
                search: quickSearchApi,
                source: providers.filter(({ value }) => value).map(({ id }) => id),
                ...(leadsZeroParams !== null && {
                    leads_zero: leadsZeroParams,
                }),
                inventory: userAuth.inventory.value,
            },
        }),
        [selectedDol.value, activeTableParams, dolRangeParams, dateRange.startDate, dateRange.endDate, userAuth.dealerV2.uid, userAuth.inventory.value, page, pageSize, sortedBy, quickSearchApi, providers, leadsZeroParams]
    );

    const { data, isFetching } = useGetStatistics<EModules.AdvancedReportsV2>(fetchParameters);

    useEffect(() => {
        // update url

        console.log('activeTableParams', activeTableParams, selectedDol.value);

        setMultipleParams({
            // startDate: format(dateRange.startDate, apiDateFormat),
            // endDate: format(dateRange.endDate, apiDateFormat),
            activeTable: activeTableParams ?? undefined,
            // ...(quickSearchApi && {
            //   search: quickSearchApi
            // }),
            // ...(sortBy.order && sortBy.column && {
            //   sortBy: sortBy.order === ESortOrder.Ascending ? sortBy.column : `-${sortBy.column}`,
            // }),
            ...(activeTableParams === EOrderingReports.DaysOnLot && selectedDol.value === 0 && {
                dolRange: `${dolRange[0]}-${dolRange[1]}`
            }),
            // ...(leadsZeroParams && {
            leadsZero: leadsZeroParams ?? undefined,
            // }),
            // ...(activeTableParams === EOrderingReports.DaysOnLot && selectedDol.value !== 0 && {
            selectedDolRange: selectedDol ? selectedDol.value.toString() : undefined,
            // })
            inventory: userAuth.inventory.value,
            dealership_uid: userAuth.dealerV2.uid,
            ...(!dateRange.key ? {
                startDate: format(dateRange.startDate, apiDateFormat),
                endDate: format(dateRange.endDate, apiDateFormat),
            } : {
                date_range: dateRangeParams || 'all_time',
            })
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        // dateRange.startDate, 
        // dateRange.endDate, 
        activeTableParams, 
        selectedDol, 
        dolRange, 
        leadsZeroParams, 
        userAuth.inventory.value,
        userAuth.dealerV2.uid
    ]);

    const onChangerOrder = useCallback((newTabIndex: number, newOrder?: string | number) => {

        setDateRange(newOrder === EOrderingReports.DaysOnLot ? commonDateRangesV2[2].value : commonDateRangesV2[2].value);

        console.log('newOrder', newOrder);

        // dispatch(setActiveTab(newOrder as EOrderingReports));

        removeMultipleParams(['sortBy', 'selectedDolRange']);

        setSelectedDol(dolOptions[0]);

        setDolRange([1, 2000]);

        setMultipleParams({
            [EQueryParameterKeys.ActiveTable]: newOrder as EOrderingReports,
            date_range: 'all_time',
        });

        setPage(0);
        
        setSortBy([]);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onSearch = (search: string) => {
        setQuickSearch(search);
        setPage(0);
        setMultipleParams({
            search
        });
    };

    const onSort = useCallback((sort: SortingState) => {
        // if (!sort.length) return;
        setSortBy(sort);
        setMultipleParams({
            sortBy: sort.length > 0 ? `${sort[0].desc ? '-' : ''}${sort[0].id}` : '',
        });
    }, []);

    const activeIndexQuickOption = useMemo(
        () =>
            commonDateRangesV2.findIndex(
                ({ value: quickOptionValue }) =>
                    format(quickOptionValue.startDate, apiDateFormat) ===
                    format(dateRange.startDate, apiDateFormat) &&
                    format(quickOptionValue.endDate, apiDateFormat) ===
                    format(dateRange.endDate, apiDateFormat)
            ),
        [dateRange.endDate, dateRange.startDate]
    );

    const onGeneratePDF: TActionProps<TGeneratePdfFunction> = useCallback(
        async (onGeneratePDF, setIsLoading) => {
            setIsLoading(true);

            try {
                let hasNextPage = true;
                let collectedResults: TAdvancedReportResultsV2[] = [];
                // let mapOffset = defaultOffset;

                let tempPage = 1;

                do {
                    const { next, results } = await getStatisticsAsync<EModules.AdvancedReportsV2>({
                        ...fetchParameters,
                        params: {
                            ...fetchParameters.params,
                            page: tempPage,
                            page_size: 1000
                            // offset: mapOffset,
                        },
                    })();

                    collectedResults = [...collectedResults, ...results];

                    tempPage++;
                    // console.log('next', next);

                    hasNextPage = next ? true : false;
                    // mapOffset = mapOffset + defaultPageSize;
                } while (hasNextPage);


                // filter providers
                const filteredProviders = providers.filter(({ value }) => value).map((providers) => {
                    // cgr_deal_rating
                    // atc_deal_rating
                    // ccm_deal_rating
                    return providers.id.toLowerCase();
                });

                // create a filter in pdfColumns to filter out the columns that are not in the filteredProviders with a same prefix in the key deal_rating then all other key should include without the deal_rating
                const filteredPDFColumns = getPdfColumns(userAuth.inventory.value, userAuth.dealerV2, columnVisibility).filter(({ key }) => {
                    if (key.includes('_deal_rating')) {
                        return filteredProviders.includes(key.split('_deal_rating')[0]);
                    }
                    return true;
                });

                const bodyFormatted = collectedResults.map((values) => {
                    return filteredPDFColumns.map(
                        ({ func, key }) =>
                            func?.(values[key], values) || (values[key] as string)
                    );
                });

                const dateRangeTitle = activeIndexQuickOption !== -1 ? `${commonDateRangesV2[activeIndexQuickOption].label} (${formattedDateRange(dateRange)})` : formattedDateRange(dateRange);

                const tablName = tableTabs.find((tableTab) => tableTab.id === activeTableParams)!.label;

                const filename = reportNaming({
                    dealersshipName: userAuth.dealerV2.name,
                    report: capitalizeFirstLetter(tablName),
                    inventory: userAuth.inventory.value,
                    extension: 'pdf'
                });

                onGeneratePDF(
                    {
                        head: [filteredPDFColumns.map(({ label }) => label)],
                        body: bodyFormatted,
                        ...onGeneratePDFDefaultType(`${capitalizeFirstLetter(tablName)}`, dateRangeTitle)
                    },
                    filename,
                    {
                        orientation: "landscape",
                        compress: true
                    }
                );
                setIsLoading(false);
            } catch (err) {
                toast.error("Can't export PDF file. Please contact your admin");
            }
        },
        [providers, userAuth.inventory.value, userAuth.dealerV2, activeIndexQuickOption, dateRange, activeTableParams, fetchParameters, columnVisibility]
    );

    const onGenerateXLSX: TActionProps<TGenerateXLSFunction> = useCallback(
        async (generateXLS, setIsLoading) => {
            setIsLoading(true);

            try {
                let hasNextPage = true;
                let collectedResults: TAdvancedReportResultsV2[] = [];
                // let mapOffset = defaultOffset;

                let tempPage = 1;

                do {
                    const { next, results } = await getStatisticsAsync<EModules.AdvancedReportsV2>({
                        ...fetchParameters,
                        params: {
                            ...fetchParameters.params,
                            page: tempPage,
                            page_size: 1000
                            // offset: mapOffset,
                        },
                    })();

                    collectedResults = [...collectedResults, ...results];

                    tempPage++;
                    // console.log('next', next);

                    hasNextPage = next ? true : false;
                    // mapOffset = mapOffset + defaultPageSize;
                } while (hasNextPage);


                // filter providers
                const filteredProviders = providers.filter(({ value }) => value).map((providers) => {
                    return providers.id.toLowerCase();
                });

                // create a filter in pdfColumns to filter out the columns that are not in the filteredProviders with a same prefix in the key deal_rating then all other key should include without the deal_rating
                const filteredXLSColumns = getXLSColumns(userAuth.inventory.value, userAuth.dealerV2, columnVisibility).filter(({ key }) => {
                    if (key.includes('_deal_rating')) {
                        return filteredProviders.includes(key.split('_deal_rating')[0]);
                    }
                    return true;
                });

                const bodyFormatted = collectedResults.map((values) => {
                    return filteredXLSColumns.map(
                        ({ func, key }) =>
                            func?.(values[key], values) || (values[key] as string)
                    );
                });

                const tablName = tableTabs.find((tableTab) => tableTab.id === activeTableParams)!.label;

                const dateRangeTitle = activeIndexQuickOption !== -1 ? `${commonDateRangesV2[activeIndexQuickOption].label} (${formattedDateRange(dateRange)})` : formattedDateRange(dateRange);

                const filename = reportNaming({
                    dealersshipName: userAuth.dealerV2.name,
                    report: capitalizeFirstLetter(tablName),
                    inventory: userAuth.inventory.value,
                    extension: 'xlsx'
                });

                generateXLS(
                    {
                        head: [filteredXLSColumns.map(({ label }) => label)],
                        body: bodyFormatted,
                        title: capitalizeFirstLetter(tablName),
                        formattedDate: dateRangeTitle
                    },
                    filename,
                );
                setIsLoading(false);
            } catch (err) {
                toast.error("Can't export XLS file. Please contact your admin");
            }
        },
        [providers, userAuth.inventory.value, userAuth.dealerV2, activeTableParams, fetchParameters, columnVisibility]
    );

    const filteredColumns = useMemo(
        () => filterColumnsV2(getColumns(userAuth.inventory.value, userAuth.dealerV2), providers),
        [providers, userAuth.dealerV2, userAuth.inventory.value]
    );

    const [columnPositions, setColumnPositions] = useState<string[]>(
        defaultFilters.columnPositions || filteredColumns.map((column) => column.id!)
    );

    // const saveSelectedFilters = useCallback(() => {
    //     if (currentTableFilter) {
    //         setDefaultFilters<ITableWithProviders>(currentTableFilter, {
    //             dateRange,
    //             pageSize,
    //             providers,
    //             columnVisibility,
    //             columnSizing,
    //         });
    //     }
    // }, [currentTableFilter, dateRange, pageSize, providers, columnVisibility, columnSizing]);

    // useEffect(() => {
    //     saveSelectedFilters();
    // }, [dateRange, pageSize, activeTableParams, providers, saveSelectedFilters]);

    const onPageChange = useCallback((pagination: PaginationState) => {
        setPage(pagination.pageIndex);
        setPageSize(pagination.pageSize);
    }, []);

    const onDateChange = (newValue: [Date, Date], key?: TDateRangeOption) => {
        // setDateRange({ startDate, endDate });

        if (!key) {

          setMultipleParams({
            startDate: format(set(newValue[0], { hours: 0, minutes: 0, seconds: 0 }), apiDateFormat),
            endDate: format(set(newValue[1], { hours: 23, minutes: 59, seconds: 59 }), apiDateFormat),
            date_range: undefined,
          })

        } else {

            removeMultipleParams(['startDate', 'endDate']);

            setMultipleParams({
              date_range: key,
            });
            
        }
        setDateRange({
            startDate: set(newValue[0], { hours: 0, minutes: 0, seconds: 0 }),
            endDate: set(newValue[1], { hours: 23, minutes: 59, seconds: 59 }),
        });
    };

    const onProviderChange = (selectedProvider: IProviderCheckList) => {
        setProviders((prevProviders) =>
            prevProviders.map(provider =>
                provider.id === selectedProvider.id ? { ...provider, value: !provider.value } : provider
            )
        );

        setSortBy([]);

    };

    const onDolChange = (selectedDol: IDropdownFilterKeyPair) => {
        setSelectedDol(selectedDol);
        // setSortBy([{ id: 'dol', desc: false }]);
    };

    const onSavePreferences = () => {

        if (!currentTableFilter) return;
    
        const pagePrefrence = setDefaultFiltersV2<ITableWithProviders>(currentTableFilter, {
            dateRange: {
                startDate: dateRange.startDate,
                endDate: dateRange.endDate,
                key: dateRangeParams || undefined
            },
            pageSize,
            providers,
            columnVisibility,
            columnSizing,
            columnPositions
        });
    
        console.log(JSON.parse(pagePrefrence));
    
        savePagePreference({
            settings: JSON.parse(pagePrefrence),
        }, {
            onError: () => {
              toast.error("Failed to save preferences");
            }, 
            onSuccess: () => {
              toast.success("Page preferences saved successfully");
            }
        });
    };

    const getActiveTable = tableTabs.findIndex((tableTab) => tableTab.id === activeTableParams);

    const columnSidebarChilrend = () => {
        if (!providers.length || userAuth.inventory.value === 'new') return null;
        return (
            <Box
                display="flex"
                flexFlow="column"
                gap="10px"
            >
                <Text
                    fontWeight="600"
                >
                    Providers
                </Text>
                <CustomizeProvider
                    providers={providers}
                    onChange={onProviderChange}
                />
            </Box>
        );
    };

    const conttroller = () => {
        return (
            <Box
                display="flex"
                gap="15px"
                justifyContent="space-between"
                flex="1"
                alignItems="center"
            >
                <Box
                    display="flex"
                    alignItems="center"
                    gap="15px"
                    flex="1"
                >
                    {(activeTableParams === EOrderingReports.DaysOnLot) && (
                        <DolSelect
                            defaultValue={selectedDol}
                            options={dolOptions}
                            onChange={onDolChange}
                        />
                    )}
                    {(activeTableParams === EOrderingReports.VDPSDOL) && (
                        <DolSelect
                            defaultValue={selectedDol}
                            options={vdpsDolOptions}
                            onChange={onDolChange}
                        />
                    )}
                </Box>
                <Box>
                    <ActionButtonV2
                        actionConfig={{
                            isDownloadPDF: true,
                            isDownloadXLS: true,
                            isEmailPDF: true,
                            isEmailXLS: true,
                            isPrintPDF: true,
                        }}
                        onDownloadPDF={onGeneratePDF}
                        onDownloadXLS={onGenerateXLSX}
                        onGeneratePDF={onGeneratePDF}
                    />
                </Box>
            </Box>
        );
    };

    return (
        <Box 
            id={ERoutesAnchors.ReportingTables}
            display="flex"
            flexDirection="column"
            flex={1}    
        >
            <Typography
                className={styles["reporting__tableLabel"]}
                fontSize="text-xl"
                fontWeight="bold"
            >
                Key Results
            </Typography>
            <Box
                display="flex"
                flexDirection="column"
                gap="15px"
                flex={1}
            >
                <Tabs
                    active={getActiveTable}
                    tabs={tableTabs}
                    onClickTab={onChangerOrder}
                />
                {/* {activeTableParams !== EOrderingReports.VDPBoost && ( */}

                    <Box
                        display="flex"
                        flexDirection="column"
                        gap="15px"
                        flex={1}
                    >
                        <DateRange
                            onChange={onDateChange}
                            quickOptions={commonDateRangesV2}
                            value={dateRange}
                        />
                        <DataTable<TAdvancedReportResultsV2>
                            data={data?.results || []}
                            rowCount={data?.count || 0}
                            onPageChange={onPageChange}
                            sort={sortBy}
                            onSort={onSort}
                            columns={filteredColumns as any}
                            pageIndex={page}
                            pageSize={pageSize}
                            isLoading={isFetching}
                            conttroller={conttroller()}
                            search={quickSearchApi}
                            onSearch={onSearch}
                            pageCount={data?.total_pages}
                            onColumnVisibilityChange={setColumnVisibility}
                            columnVisibility={columnVisibility}
                            onColumnSizingChange={setColumnSizing}
                            columnSizing={columnSizing}
                            showColumns={true}
                            columnsSidebarChildren={!userAuth.dealerV2.is_lite ? columnSidebarChilrend() : undefined}
                            // onUpdateData={updateData}
                            onSavePreferences={onSavePreferences}
                            columnPinning={{
                                left: ['stock_number']
                            }}
                            columnPositions={columnPositions}
                            onColumnOrderChange={setColumnPositions}
                        />
                    </Box>
                {/* )} */}
                {/* {activeTableParams === EOrderingReports.VDPBoost && (
                    <VDPBoostReporting />
                )} */}
            </Box>
        </Box>
    );
};

export default ReportingTabs;
